import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import type { User, UserRole } from '@/types';
import { apiClient } from '@/services/api-client';
import { useAuthStore } from '@/store/auth-store';

interface RoleUpdateDialogProps {
  user: User | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function RoleUpdateDialog({ user, open, onOpenChange, onSuccess }: RoleUpdateDialogProps) {
  const [role, setRole] = useState<UserRole | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isSuperAdmin } = useAuthStore();

  useEffect(() => {
    if (open) {
      setRole(null);
      setError(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!user || !role) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.put(`/api/v1/admin/users/${user.id}/role`, { role });
      if (response.success) {
        onOpenChange(false);
        onSuccess?.();
      } else {
        setError(response.error?.message || 'Failed to update role');
      }
    } catch {
      setError('An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) return null;

  const canAssignSuperAdmin = isSuperAdmin();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change User Role</DialogTitle>
          <DialogDescription>
            Update the role for {user.firstName || user.email}. This will change their access
            permissions.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="role">New Role</Label>
            <Select value={role || user.role} onValueChange={(value) => setRole(value as UserRole)}>
              <SelectTrigger id="role">
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                {canAssignSuperAdmin && <SelectItem value="super_admin">Super Admin</SelectItem>}
              </SelectContent>
            </Select>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          {!canAssignSuperAdmin && (
            <p className="text-sm text-muted-foreground">
              Only super admins can assign the super admin role.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading || !role || role === user.role}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Update Role
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
